if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // ✅ ADD THIS: Get boost variant ID from window (set from theme.liquid)
        this.BOOST_VARIANT_ID = window.BOOST_VARIANT_ID || null;
      }

      // ✅ ADD THIS METHOD: Check if boost product is already in cart
      async isBoostInCart() {
        if (!this.BOOST_VARIANT_ID) return false;
        
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          const hasBoost = cart.items.some(item => item.variant_id == this.BOOST_VARIANT_ID);
          console.log("🔍 Product Form - Boost already in cart:", hasBoost);
          return hasBoost;
        } catch (e) {
          console.error("❌ Error checking cart:", e);
          return false;
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // ✅ ADD THIS: Modify request to include boost product
        this.addBoostProductToRequest(formData, config)
          .then((modifiedConfig) => {
            return fetch(`${routes.cart_add_url}`, modifiedConfig);
          })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // ✅ ADD THIS METHOD: Add boost product to the request
      async addBoostProductToRequest(formData, config) {
        // If no boost variant configured, return original config
        if (!this.BOOST_VARIANT_ID) {
          config.body = formData;
          return config;
        }

        const originalVariantId = formData.get('id');
        
        // If user is adding the boost product itself, don't add it again
        if (originalVariantId == this.BOOST_VARIANT_ID) {
          console.log("ℹ️ User is adding the boost product itself");
          config.body = formData;
          return config;
        }

        // Check if boost is already in cart
        const boostExists = await this.isBoostInCart();
        
        if (boostExists) {
          console.log("⏭️ Boost product already in cart - skipping addition");
          config.body = formData;
          return config;
        }

        // Build items array with original product + boost product
        const items = [
          {
            id: originalVariantId,
            quantity: parseInt(formData.get('quantity') || 1),
            properties: {}
          },
          {
            id: this.BOOST_VARIANT_ID,
            quantity: 1,
            properties: {
              _auto_added: 'theme_setting_boost'
            }
          }
        ];

        // Extract custom properties from FormData
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('properties[')) {
            const propName = key.match(/properties\[(.+)\]/)[1];
            items[0].properties[propName] = value;
          }
        }

        console.log("✨ Product Form - Adding boost product");
        console.log("📦 Cart items:", items);

        // Convert to JSON format
        config.body = JSON.stringify({ items });
        config.headers['Content-Type'] = 'application/json';

        // Preserve sections data for cart drawer/notification update
        if (this.cart) {
          const bodyObj = JSON.parse(config.body);
          bodyObj.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
          bodyObj.sections_url = window.location.pathname;
          config.body = JSON.stringify(bodyObj);
        }

        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}